import { Button, IconButton } from "@ai-teach/ui-web";
import { gql } from "@apollo/client/core";
import { useMutation, useQuery } from "@apollo/client/react";
import { useState } from "react";
import toast from "react-hot-toast";

const USERS_AND_SESSIONS_QUERY = gql`
  query UsersAndSessions {
    users {
      id
      username
      email
      name
      role
      createdAt
      accessLockedUntil
    }
    activeSessions {
      session {
        id
        expiresAt
        createdAt
        ipAddress
        userAgent
      }
      user {
        id
        username
        email
        name
        role
      }
    }
  }
`;

const LOCK_USER_MUTATION = gql`
  mutation LockUser($id: ID!) {
    lockUser(id: $id)
  }
`;

const UNLOCK_USER_MUTATION = gql`
  mutation UnlockUser($id: ID!) {
    unlockUser(id: $id)
  }
`;

interface User {
  id: string;
  username: string;
  email: string;
  name: string;
  role: string;
  createdAt: string;
  accessLockedUntil?: string | null;
}

/** A lock whose time has passed is spent — the API ignores it, so the UI must not show it. */
const isLocked = (user: User): boolean =>
  !!user.accessLockedUntil && new Date(user.accessLockedUntil) > new Date();

interface Session {
  id: string;
  expiresAt: string;
  createdAt: string;
  ipAddress?: string;
  userAgent?: string;
}

interface ActiveSession {
  session: Session;
  user: {
    id: string;
    username: string;
    email: string;
    name: string;
    role: string;
  };
}

interface QueryData {
  users: User[];
  activeSessions: ActiveSession[];
}

export function UsersPage() {
  const { data, loading, error, refetch } = useQuery<QueryData>(USERS_AND_SESSIONS_QUERY);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [lockUser] = useMutation(LOCK_USER_MUTATION);
  const [unlockUser] = useMutation(UNLOCK_USER_MUTATION);

  const handleLock = async (user: User) => {
    if (
      !window.confirm(
        `Lock ${user.username}? They will be signed out of the API until an admin unlocks them.`
      )
    ) {
      return;
    }
    setPendingId(user.id);
    try {
      await lockUser({ variables: { id: user.id } });
      await refetch();
      toast.success(`Locked ${user.username}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not lock this account");
    } finally {
      setPendingId(null);
    }
  };

  const handleUnlock = async (user: User) => {
    setPendingId(user.id);
    try {
      await unlockUser({ variables: { id: user.id } });
      await refetch();
      toast.success(`Unlocked ${user.username}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not unlock this account");
    } finally {
      setPendingId(null);
    }
  };

  const lockedUsers = data?.users.filter(isLocked) ?? [];
  const openUsers = data?.users.filter((user) => !isLocked(user)) ?? [];

  if (error) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="h3 mb-6">Users & Sessions</h1>
          <div className="bg-error-500/10 border border-error-500 text-error-400 rounded-lg p-4">
            Error loading data: {error.message}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="h3">Users & Sessions</h1>
          <Button type="button" onClick={() => refetch()} disabled={loading} variant="primary">
            {loading ? "Loading..." : "Refresh"}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="panel p-6">
            <div className="caption mb-1">Total Users</div>
            <div className="h3">{data?.users.length || 0}</div>
          </div>
          <div className="panel p-6">
            <div className="caption mb-1">Active Sessions</div>
            <div className="text-3xl font-bold text-green-600">
              {data?.activeSessions.length || 0}
            </div>
          </div>
          <div className="panel p-6">
            <div className="caption mb-1">Locked Accounts</div>
            <div className={`h3 ${lockedUsers.length > 0 ? "text-error-400" : ""}`}>
              {lockedUsers.length}
            </div>
          </div>
        </div>

        {/* Locked accounts: listed separately because each one is a person who cannot work, and
            kept on the page when empty so it reads as a thing being watched rather than a section
            that only exists once something has gone wrong. Alarming styling only when it is. */}
        <div
          className={`data-table-container mb-6 ${
            lockedUsers.length > 0 ? "border border-error-500" : ""
          }`}
        >
          <div className="data-table-header">
            <h2 className={`h5 ${lockedUsers.length > 0 ? "text-error-400" : ""}`}>
              Locked Accounts ({lockedUsers.length})
            </h2>
          </div>
          {lockedUsers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No accounts are locked</div>
          ) : (
            <div className="overflow-x-auto gradient-scrollbar">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Locked until</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {lockedUsers.map((user: User) => (
                    <tr key={user.id}>
                      <td>
                        <div className="data-table-cell-name">{user.username}</div>
                      </td>
                      <td>
                        <span className="data-table-cell-caption">{user.name}</span>
                      </td>
                      <td>
                        <span className="uppercase badge-secondary">{user.role}</span>
                      </td>
                      <td>
                        <span className="data-table-cell-caption">
                          {new Date(user.accessLockedUntil as string).toLocaleString()}
                        </span>
                      </td>
                      <td>
                        <Button
                          type="button"
                          variant="primary"
                          onClick={() => handleUnlock(user)}
                          disabled={pendingId === user.id}
                        >
                          {pendingId === user.id ? "Unlocking..." : "Unlock"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* All Users Table */}
        <div className="data-table-container mb-6">
          <div className="data-table-header">
            <h2 className="h5">All Users</h2>
          </div>
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : openUsers.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No users found</div>
          ) : (
            <div className="overflow-x-auto gradient-scrollbar">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Created</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {openUsers.map((user: User) => (
                    <tr key={user.id}>
                      <td>
                        <div className="data-table-cell-name">{user.username}</div>
                      </td>
                      <td>
                        <span className="data-table-cell-caption">{user.name}</span>
                      </td>
                      <td>
                        <span className="data-table-cell-caption">{user.email}</span>
                      </td>
                      <td>
                        <span
                          className={`uppercase ${
                            ["super", "admin", "editor", "developer", "educational_cons"].includes(
                              user.role
                            )
                              ? "badge-accent"
                              : user.role === "teacher"
                                ? "badge-primary"
                                : "badge-secondary"
                          }`}
                        >
                          {user.role}
                        </span>
                      </td>
                      <td>
                        <span className="data-table-cell-caption">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => handleLock(user)}
                          disabled={pendingId === user.id}
                        >
                          {pendingId === user.id ? "Locking..." : "Lock"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Active Sessions Table */}
        <div className="data-table-container">
          <div className="data-table-header">
            <h2 className="h5">Active Sessions</h2>
          </div>
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : data?.activeSessions.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No active sessions</div>
          ) : (
            <div className="overflow-x-auto gradient-scrollbar">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Session Started</th>
                    <th>Expires At</th>
                    <th>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.activeSessions.map((activeSession: ActiveSession) => (
                    <tr key={activeSession.session.id}>
                      <td>
                        <div className="data-table-cell-name">{activeSession.user.username}</div>
                      </td>
                      <td>
                        <span className="data-table-cell-caption">{activeSession.user.email}</span>
                      </td>
                      <td>
                        <span
                          className={`uppercase ${
                            ["super", "admin", "editor", "developer", "educational_cons"].includes(
                              activeSession.user.role
                            )
                              ? "badge-accent"
                              : activeSession.user.role === "teacher"
                                ? "badge-primary"
                                : "badge-secondary"
                          }`}
                        >
                          {activeSession.user.role}
                        </span>
                      </td>
                      <td>
                        <span className="data-table-cell-caption">
                          {new Date(activeSession.session.createdAt).toLocaleString()}
                        </span>
                      </td>
                      <td>
                        <span className="data-table-cell-caption">
                          {new Date(activeSession.session.expiresAt).toLocaleString()}
                        </span>
                      </td>
                      <td>
                        <span className="data-table-cell-caption">
                          {activeSession.session.ipAddress || "N/A"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
